import { BaseControl } from "../base-control";
import { resolveConsentImageText, resolveConsentMessage } from "../../constants/signature-consent.constant";

const CONSENT_BAR_HEIGHT = 28;

class SignatureControlCtrl extends BaseControl {
  emptyModel = {display: {}, value: {}};
  
  fileUrl = null;
  copy = null;
  modal = null;
  uibModal = null;
  
  fieldWidth = null;
  fieldHeight = null;
  aspect = null;
  imageSrc = "";

  constructor(BaseControlService, TBService, $scope, $uibModal, $element, $sce, $timeout) {
    'ngInject';
    super(BaseControlService, $scope);
    Object.assign(this, { $uibModal, $element, $sce, $timeout });
    this.tb = TBService;
  }

  addConsentToSignatureDataUrl(dataUrl, consentText) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        const signatureAreaHeight = height - CONSENT_BAR_HEIGHT;
        const scale = Math.min(width / img.naturalWidth, signatureAreaHeight / img.naturalHeight);
        const scaledW = img.naturalWidth * scale;
        const scaledH = img.naturalHeight * scale;
        const x = (width - scaledW) / 2;
        const y = (signatureAreaHeight - scaledH) / 2;
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, x, y, scaledW, scaledH);

        let fontSize = 18;
        const padding = 16;
        const maxTextWidth = width - padding;
        const measureCanvas = document.createElement('canvas');
        const mCtx = measureCanvas.getContext('2d');
        for (; fontSize >= 10; fontSize--) {
          mCtx.font = fontSize + 'px sans-serif';
          if (mCtx.measureText(consentText).width <= maxTextWidth) break;
        }
        ctx.fillStyle = '#000000';
        ctx.font = fontSize + 'px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(consentText, width / 2, height - CONSENT_BAR_HEIGHT / 2);
        resolve(canvas.toDataURL());
      };
      img.onerror = () => reject(new Error('Failed to load signature image'));
      img.src = dataUrl;
    });
  }

  async initializeControlData() {
    await super.initializeControlData();
    this.fileUrl = await this.buildURL();
    this.$scope.$applyAsync();
    if (this.isPrint && this.fileUrl) await this.preloadImage();
  }

  modelChanged(newVal, oldVal) {
    super.modelChanged(newVal, oldVal);
    this.updateFlags();
  }

  updateFlags() {
    this.hasImage = this.model.value?.body || this.model.value?.url;
    this.setImgSrc();
  }

  setImgSrc() {
    if (this.model.value?.body) this.imageSrc = `data:image/png;base64,${this.model.value?.body}`;
    else if (this.model.value?.url) this.imageSrc = this.fileUrl;
  }

  async preloadImage() {
    const img = new Image();
    img.src = this.fileUrl;
    img.className = 'bounded-img';
    try { await img.decode() } // Resolves when image is decoded and safe to append to DOM.
    catch { console.error(`Error downloading image: ${this.fileUrl}`) }
    this.$element.find("#printed-image").replaceWith(img);
  }

  isSame(newVal={}, oldVal={}) {
    return angular.equals(newVal, oldVal);
  }

  openModal() {
    if (this.modal) return null;

    const uibModal = { scope: this.$scope, size: "lg control-modal", backdrop: false };
    const $fieldEl = $(this.$element[0]).find('.form-control')[0];
    this.fieldWidth = $fieldEl.offsetWidth;
    this.fieldHeight = $fieldEl.offsetHeight;
    this.aspect = this.fieldHeight / this.fieldWidth;

    this.copy = angular.copy(this.model);

    this.color = $(this.$element[0]).css("color") || 'black';
    this.font = this.isDynamic ?  $(this.$element[0]).css("font-family") : (this.tb.getCssObj(this.token.layout) || {})["font-family"];

    const signFeats = this.token.options?.signatureFeatures || [];
    this.disableKeyboard = signFeats.length == 1 && signFeats[0] == "drawpad";
    this.disablePad = signFeats.length == 1 && signFeats[0] == "keyboard";
    this.requireConsentAgreement = !!this.token.options?.requireConsentAgreement;
    this.consentText = resolveConsentMessage(this.token.options);

    uibModal.template =
      `<signature-modal
          has-signature-data="$ctrl.hasSignatureData"
          cancel-modal="$ctrl.cancelModal(opt)"
          clear-sig="$ctrl.clearSig(clear)"
          accept-sig="$ctrl.acceptSig(accept)"
          aspect="$ctrl.aspect"
          field-width="$ctrl.fieldWidth"
          label="$ctrl.label"
          required="$ctrl.required"
          require-consent-agreement="$ctrl.requireConsentAgreement"
          consent-text="$ctrl.consentText"
          key-down="$ctrl.modalKeyDown(keyDownData)"
          disable-keyboard="$ctrl.disableKeyboard"
          disable-pad="$ctrl.disablePad"
          font="$ctrl.font"
          color="$ctrl.color">
        </signature-modal>`;
    
    this.modal = this.dom.openModal(this.$uibModal, uibModal, { triggerElement: this.$element });
    
    this.modal.result.then(
      closed => { 
        this.copy = null;
        this.cancelModal();
      },
      canceled => { 
        this.reset();
        this.cancelModal();
      }
    );
  }
  
  reset() {
    if (this.copy) this.model = angular.copy(this.copy);
  }
  
  modalKeyDown(keyDownData) {
    if (!this.modal || !keyDownData) return null;
    const { event, submit, accept } = keyDownData;
    const acceptFn = accept || keyDownData;

    switch (event?.key) {
      case 'Enter':
        if (submit) return submit();
        return (!this.required || this.hasSignatureData) ? this.acceptSig(acceptFn) : null;
      case 'Escape':
        this.reset();
        return this.cancelModal();
      case 'Tab':
        return this.handleTabKey(acceptFn);
      default:
        return;
    }
  }
  
  handleTabKey(accept){
    var signature = accept();
    if (!signature.dataUrl || signature.isEmpty) {
      this.cancelModal();
    } else {
      this.$timeout(() => { 
        this.$scope.$apply(); 
        if (!this.required || this.hasSignatureData) this.acceptSig(accept);
      });
    }
  }

  acceptSig(accept) {
    const signature = accept();
    if (!signature.dataUrl || signature.isEmpty) {
      this.clearSig();
      this.doneModal();
      return;
    }
    const applySignature = (dataUrl) => {
      const now = new Date();
      const dtTime = now.toLocaleString().replace(/,| /g, '_').replace(/\//g, '-');
      const time = now.getTime();
      this.model.value = {
        filename: `signature_${dtTime}_${time}.png`,
        body: dataUrl.replace('data:image/png;base64,', ''),
        ignoreEncoding: true,
        isSignature: true
      };
      this.doneModal();
    };
    if (this.requireConsentAgreement) {
      const imageConsentText = resolveConsentImageText(this.token.options);
      this.addConsentToSignatureDataUrl(signature.dataUrl, imageConsentText)
        .then((dataUrl) => this.$timeout(() => applySignature(dataUrl)))
        .catch(() => this.$timeout(() => applySignature(signature.dataUrl)));
    } else {
      applySignature(signature.dataUrl);
    }
  }

  clearSig(clear){
    //initiated by template-literal or acceptSig(), (when no value)
    this.clearModel();
    if(clear) clear();
  }

  cancelModal(nextAction) { 
    this.modal?.dismiss(nextAction); 
    this.$timeout(() => this.modal = null);
  }

  doneModal() { 
    this.modal?.close();
    this.$timeout(() => this.modal = null);
  }
  
  async buildURL() {
    const { url } = (this.model?.value || {});
    if (!url) return null;

    return await this.ts.getTemplateAttachmentUrl(url);
  }
}

export default {
  bindings: {
    token: "<",
    model: "=",
    tabNumber: "<"
  },
  templateUrl: "fe-control/controls/signature-control/signature-control.component.html",
  controller: SignatureControlCtrl
};